import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  MapPinIcon,
  CurrencyRupeeIcon,
  CalendarDaysIcon,
  BriefcaseIcon,
  SparklesIcon,
  UserIcon,
  CameraIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  MinusCircleIcon,
  FireIcon,
  ShareIcon
} from '@heroicons/react/24/outline';
import { formatDistanceToNow, format } from 'date-fns';
import { Opportunity, OpportunityApplication, Recommendation } from '../../types';
import Avatar from '../common/Avatar';
import ShareModal from '../common/ShareModal';
import { OPPORTUNITY_TYPES as OPP_CONSTANTS } from '../../constants/opportunities';

import { useAppSelector } from '../../hooks/redux';
import LoginPromptModal from '../common/LoginPromptModal';

interface OpportunityCardProps {
  opportunity: Opportunity | Recommendation;
  userApplication?: OpportunityApplication;
}

const OpportunityCard: React.FC<OpportunityCardProps> = ({ opportunity, userApplication }) => {
  const { user } = useAppSelector((state) => state.auth);
  const [showShareModal, setShowShareModal] = React.useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = React.useState(false);
  
  const handleShareClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowShareModal(true);
  };

  // Debug logging
  if (userApplication) {
    // console.log(`OpportunityCard [${opportunity.id}]:`, { status: userApplication.status });
  }

  const isRecommendation = (opp: Opportunity | Recommendation): opp is Recommendation => {
    return 'match_score' in opp;
  };

  const getOpportunityTypeLabel = (type: string) => {
    switch (type) {
      case OPP_CONSTANTS.CREATOR: return 'Creator';
      case OPP_CONSTANTS.PHOTOGRAPHER: return 'Photographer';
      case OPP_CONSTANTS.VIDEOGRAPHER: return 'Videographer';
      case OPP_CONSTANTS.BOTH_CREATORS: return 'Photographer & Videographer';
      case OPP_CONSTANTS.MODEL: return 'Model';
      case OPP_CONSTANTS.BOTH: return 'Creator & Model';
      case OPP_CONSTANTS.CREATIVE_COLLABORATION: return 'Collaboration';
      default: return type.replace(/_/g, ' ');
    }
  };

    const getStatusOverlay = () => {
      if (!userApplication) return null;
  
      const statusConfig = {
        accepted: {
          icon: <CheckCircleIcon className="w-16 h-16 text-green-400 mx-auto mb-2" />,
          label: 'Accepted',
          bgColor: 'bg-black/40'
        },
        rejected: {
          icon: <XCircleIcon className="w-16 h-16 text-red-400 mx-auto mb-2" />,
          label: 'Rejected',
          bgColor: 'bg-black/40'
        },
        pending: {
          icon: <ClockIcon className="w-16 h-16 text-blue-400 mx-auto mb-2" />,
          label: 'Applied',
          bgColor: 'bg-black/40'
        },
        withdrawn: {
          icon: <MinusCircleIcon className="w-16 h-16 text-gray-400 mx-auto mb-2" />,
          label: 'Withdrawn',
          bgColor: 'bg-black/40'
        }
      };

      const config = statusConfig[userApplication.status as keyof typeof statusConfig];
      if (!config) return null;

      return (
        <div className={`absolute inset-0 ${config.bgColor} backdrop-blur-[2px] z-20 flex items-center justify-center rounded-xl`}>
          <div className="text-center">
            {config.icon}
            <span className="text-white text-lg font-semibold">{config.label}</span>
          </div>
        </div>
      );
    };

  const creator = isRecommendation(opportunity) ? opportunity.creator_profile : opportunity.creator;
  const linkTo = opportunity.opportunity_type === 'profile' ? `/profile/${opportunity.id}` : `/opportunities/${opportunity.id}`;

  return (
    <>
    <Link to={linkTo} className="block h-full">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ y: -2 }}
        className="bg-white dark:bg-gray-800 rounded-2xl p-5 md:p-6 border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-xl hover:border-gray-200 dark:hover:border-gray-600 transition-all duration-300 h-full flex flex-col group relative z-0 overflow-hidden"
      >
        {/* Status Badge/Overlay */}
        {getStatusOverlay()}


        {/* Decorative gradient blob */}
        <div className="absolute top-0 right-0 -mt-8 -mr-8 w-24 h-24 bg-gradient-to-br from-gray-50 to-gray-100 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-5 relative z-10">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <Avatar
              src={creator?.profile_pic_thumb_signed || creator?.profile_pic_thumb_signed}
              alt={`${creator?.first_name} ${creator?.last_name}`}
              size="sm"
              className="ring-2 ring-white dark:ring-gray-800 shadow-sm flex-shrink-0"
              isDeleted={!!creator?.deleted_at || !!creator?.deletion_scheduled_at}
            />
            <div className="flex flex-col min-w-0">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white leading-tight truncate">
                {creator?.first_name} {creator?.last_name}
              </h4>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {formatDistanceToNow(new Date(opportunity.created_at), { addSuffix: true })}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
                onClick={handleShareClick}
                className="p-1.5 text-gray-400 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/30 rounded-full transition-colors relative z-20"
                title="Share Opportunity"
            >
                <ShareIcon className="h-5 w-5" />
            </button>
            <span className={`flex-shrink-0 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                opportunity.opportunity_type === OPP_CONSTANTS.CREATOR ||
                opportunity.opportunity_type === OPP_CONSTANTS.PHOTOGRAPHER ||
                opportunity.opportunity_type === OPP_CONSTANTS.VIDEOGRAPHER ||
                opportunity.opportunity_type === OPP_CONSTANTS.BOTH_CREATORS ? 'bg-black dark:bg-white text-white dark:text-black' :
                opportunity.opportunity_type === OPP_CONSTANTS.MODEL ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100' :
                'bg-gray-900 dark:bg-gray-600 text-white'
            }`}>
                {getOpportunityTypeLabel(opportunity.opportunity_type)}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 relative z-10 flex flex-col">
          <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-black dark:group-hover:text-gray-200 transition-colors line-clamp-1">
            {opportunity.title}
          </h3>

          {/* Description Excerpt */}
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-5 line-clamp-2 leading-relaxed h-10">
            {opportunity.description}
          </p>

          <div className="space-y-3 mb-5 min-h-[4.5rem]">
            <a 
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(opportunity.location || 'Remote')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-teal-400 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <MapPinIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
              <span className="truncate hover:underline">{opportunity.location || 'Remote'}</span>
            </a>
            
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
              <CurrencyRupeeIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
              {opportunity.is_paid && opportunity.budget ? (
                <span className="font-medium text-gray-900 dark:text-white">₹{opportunity.budget.toLocaleString()}</span>
              ) : (
                <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full text-xs font-medium capitalize">
                  {opportunity.compensation_type || 'Unpaid'}
                </span>
              )}
            </div>

            {opportunity.start_date && (
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                <CalendarDaysIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                <span>
                  {new Date(opportunity.start_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                </span>
              </div>
            )}
          </div>

          {/* Recommendation Info */}
          {isRecommendation(opportunity) && (
            <div className="mb-5 p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-100 dark:border-orange-900/30">
              <div className="flex items-center gap-1.5 mb-1">
                <FireIcon className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                <span className="text-xs font-bold text-orange-700 dark:text-orange-300">{opportunity.match_score}% Match</span>
              </div>
              <p className="text-[10px] text-orange-600 dark:text-orange-400 line-clamp-1">
                {opportunity.reason_for_recommendation}
              </p>
            </div>
          )}
          
          {/* Tags - Separate Row */}
          {opportunity.tags && opportunity.tags.length > 0 && (
            <div className="mt-auto pt-5 border-t border-gray-50 dark:border-gray-700 flex flex-wrap gap-1.5 mb-4">
              {opportunity.tags.slice(0, 3).map((tag) => (
                <span key={tag} className="px-2 py-0.5 bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-[10px] font-medium rounded-md border border-gray-100 dark:border-gray-600 whitespace-nowrap">
                  {tag}
                </span>
              ))}
              {opportunity.tags.length > 3 && (
                <span className="px-2 py-0.5 text-gray-400 text-[10px] font-medium whitespace-nowrap">
                  +{opportunity.tags.length - 3}
                </span>
              )}
            </div>
          )}
          
          {/* Footer / Stats */}
          <div className={`flex items-center ${
            !opportunity.tags || opportunity.tags.length === 0 ? 'mt-auto pt-5 border-t border-gray-50 dark:border-gray-700' : ''
          }`}>
             {opportunity.application_count > 0 ? (
               <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 px-2.5 py-1.5 rounded-lg w-full">
                 <BriefcaseIcon className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                 <span>{opportunity.application_count} Applicant{opportunity.application_count !== 1 ? 's' : ''}</span>
               </div>
             ) : (
               <div className="flex items-center gap-1.5 text-xs font-medium text-black dark:text-white bg-gray-100 dark:bg-gray-700 px-2.5 py-1.5 rounded-lg w-full">
                 <SparklesIcon className="h-3.5 w-3.5 text-black dark:text-white" />
                 <span>Be the first to apply!</span>
               </div>
             )}
          </div>
        </div>
      </motion.div>
    </Link>
    
    {/* Share Modal */}
    <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        title={opportunity.title}
        url={`${window.location.origin}/opportunities/${opportunity.id}`}
        type="opportunity"
        data={{
            id: opportunity.id,
            title: opportunity.title,
            subtitle: `${opportunity.location || 'Remote'} • ${opportunity.is_paid ? 'Paid' : 'Unpaid'}`,
            image: creator?.profile_pic,
            compensation_type: opportunity.compensation_type,
            budget: opportunity.budget,
        }}
    />

    {/* Login Prompt Modal */}
    <LoginPromptModal
      isOpen={showLoginPrompt}
      onClose={() => setShowLoginPrompt(false)}
      message="Sign in to share this opportunity"
      actionLabel="share"
    />
    </>
  );
};

export default OpportunityCard;


